// The keyboard and scroll arithmetic of MUI's `useAutocomplete` listbox, and
// its default option row, shared by every autocomplete ported over it
// (`community-id-autocomplete.tsx`, `multi-autocomplete-field.tsx`,
// `single-autocomplete-field.tsx`). What decides *when* each runs — the
// highlight's reason, the resync rules — stays with the component, because
// each configures `useAutocomplete` differently; this is only what is identical.

/**
 * MUI's `createFilterOptions()` defaults: `matchFrom: 'any'`, case- and
 * accent-insensitive, **no trim** (`useAutocomplete.js:30-63`). Both sides of
 * a match go through it.
 */
export function muiFilterNormalise(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** MUI's `pageSize` for PageUp/PageDown (`useAutocomplete.js`). */
export const MUI_LISTBOX_PAGE_SIZE = 5;

/**
 * `changeHighlightedIndex`'s `getNextIndex` (`useAutocomplete.js:409-440`):
 * a one-row step wraps, a page step clamps, `start`/`end` jump.
 */
export function nextHighlightIndex(
  current: number,
  diff: number | "start" | "end",
  last: number,
): number {
  if (diff === "start") return 0;
  if (diff === "end") return last;
  const next = current + diff;
  if (next < 0) return Math.abs(diff) > 1 ? 0 : last;
  if (next > last) return Math.abs(diff) > 1 ? last : 0;
  return next;
}

/**
 * `syncHighlightedIndexToDOM`'s scroll (`useAutocomplete.js:354-368`): the
 * listbox's own `scrollTop`, never an ancestor's, moved just far enough to
 * show `row` — and only when the list scrolls at all. The caller decides
 * whether this highlight scrolls (never for the pointer).
 */
export function scrollRowIntoList(list: HTMLElement, row: HTMLElement): void {
  if (list.scrollHeight <= list.clientHeight) return;
  const rowBottom = row.offsetTop + row.offsetHeight;
  if (rowBottom > list.clientHeight + list.scrollTop) {
    list.scrollTop = rowBottom - list.clientHeight;
  } else if (row.offsetTop < list.scrollTop) {
    list.scrollTop = row.offsetTop;
  }
}

/**
 * Why an option is highlighted — MUI's `highlightReasonRef`. It decides the
 * tint (`focusVisible` only for the keyboard) and whether the list scrolls to
 * it (never for the pointer). `null` is MUI's programmatic sync.
 */
export type HighlightReason = "keyboard" | "mouse" | "touch" | null;

/**
 * MUI's default listbox option, for an autocomplete that replaces no listbox
 * slot (the Community ID field, the base picker): `6px 16px`, the `body1` line, 48px tall below the `sm`
 * breakpoint and content-tall above it (`Autocomplete.js`,
 * `AutocompleteListbox`). **No `:hover` tint** — MUI tints the *highlighted*
 * option, and the pointer highlights by moving, so the tint follows the
 * highlight rather than the cursor. (`MUI_AUTOCOMPLETE_OPTION` is the
 * paginated donor's row, whose replaced listbox drops these rules.)
 */
export const MUI_LISTBOX_OPTION_ROW =
  "text-lennar-ink flex w-full cursor-pointer items-center justify-start overflow-hidden px-4 py-1.5 text-base leading-6 min-h-12 min-[600px]:min-h-0 [-webkit-tap-highlight-color:transparent]";

/**
 * The option's ground, in MUI's precedence (`AutocompleteListbox`): a
 * highlight is `action.hover`, a *keyboard* highlight `action.focus`; the
 * selected option is primary at `selectedOpacity`, plus `hoverOpacity` under a
 * highlight and plus `focusOpacity` under a keyboard one.
 */
export function muiListboxOptionGround(
  selected: boolean,
  highlighted: boolean,
  reason: HighlightReason,
) {
  const keyboard = highlighted && reason === "keyboard";
  if (selected) {
    if (keyboard) return "bg-lennar-primary-selected-focus";
    // On a touch device a non-keyboard highlight of the selected option is
    // `action.selected` instead (`@media (hover: none)`).
    if (highlighted) {
      return "bg-lennar-primary-selected-hover [@media(hover:none)]:bg-lennar-action-selected";
    }
    return "bg-lennar-primary-selected";
  }
  if (keyboard) return "bg-lennar-action-focus";
  // …and an unselected one is no tint at all.
  if (highlighted) return "bg-lennar-action-hover [@media(hover:none)]:bg-transparent";
  return undefined;
}
